import { Box, Skeleton } from "@mui/material";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useAccessibleFarmerProgrammes } from "model/useDashboardQueries";
import { usePendingInvitationCount } from "model/useProgrammeInvitationQueries";
import { buildFarmerRows } from "../corporate/FarmerTable";
import { usePerFarmerActions } from "../hooks/useInsightsDashboardQueries";
import StatTile from "./StatTile";

const STAGE_STYLES = {
  invited: { dot: "#a1a1aa", value: "#52525b" },
  onboarding: { dot: COLORS.bellflower[500], value: COLORS.bellflower[700] },
  actioning: { dot: COLORS.dahlia[500], value: COLORS.dahlia[700] },
} as const;

/**
 * Current-state breakdown of programme farmers across three mutually
 * exclusive stages:
 * - invited: has a pending invite (awaiting signup or consent) — not yet a
 *   member. Counted from the invitation API, not from membership rows.
 * - onboarding: accepted the invite / is an active member, but has no action
 *   plan yet.
 * - actioning: has at least one committed action.
 * Renders nothing when the corporate user has no farmers and no pending invites.
 */
export default function FarmerJourneyFunnel() {
  const { data: farmersData, isLoading: farmersLoading } =
    useAccessibleFarmerProgrammes();
  const { data: actionsData, isLoading: actionsLoading } =
    usePerFarmerActions();
  const { count: invited, isLoading: invitesLoading } =
    usePendingInvitationCount();

  if (farmersLoading || actionsLoading || invitesLoading) {
    return (
      <Skeleton
        variant="rounded"
        height={88}
        sx={{ borderRadius: `${BORDER_RADIUS.medium}px` }}
      />
    );
  }

  const farmers = farmersData ?? [];

  const rows = buildFarmerRows(
    farmers,
    actionsData?.data,
    undefined,
    undefined,
    null
  );

  const onboarding = rows.filter((r) => r.status === "onboarding").length;
  const actioning = rows.filter((r) => r.status === "actioning").length;

  // Nothing to show: no members and no pending invites.
  if (invited === 0 && rows.length === 0) return null;

  return (
    <Box sx={{ display: "flex", alignItems: "stretch", gap: 1 }}>
      <StatTile
        label="Invited"
        value={String(invited)}
        tooltip="Invited to the programme but not yet signed up and given consent."
        dotColor={STAGE_STYLES.invited.dot}
        valueColor={STAGE_STYLES.invited.value}
        large
      />
      <StatTile
        label="Onboarding"
        value={String(onboarding)}
        tooltip="Signed up and gave consent, but have not agreed a farm action plan yet."
        dotColor={STAGE_STYLES.onboarding.dot}
        valueColor={STAGE_STYLES.onboarding.value}
        large
      />
      <StatTile
        label="Actioning"
        value={String(actioning)}
        tooltip="Have at least one committed action in their farm action plan."
        dotColor={STAGE_STYLES.actioning.dot}
        valueColor={STAGE_STYLES.actioning.value}
        large
      />
    </Box>
  );
}
